import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const SIZES = ["6x9"];
const BINDINGS = ["Saddle Stitch"];
const FORMATS = ["Physical Copy", "Ebook (Digital Copy)"];
const BOOK_QTYS = [25, 50, 100, 250];
const ART_STYLES = ["Watercolor", "Cartoon", "Hyper-Detailed"];
const PAGES = [
  { value: 24, label: "24 Pages (Cover + 20 Inside)" },
];
const QTY_PRICES = { 25: 199, 50: 329, 100: 449, 250: 699 };

const MILESTONES = [
  {
    title: "Script & Thumbnails",
    task: "Finalize the 24-page script and thumbnail layouts.",
    timeline: "1–2 weeks",
  },
  {
    title: "Pencils & Inks",
    task: "Complete detailed sketches and finalized line art.",
    timeline: "3–6 weeks",
  },
  {
    title: "Coloring",
    task: "Apply base colors, lighting, and shading.",
    timeline: "2–3 weeks",
  },
  {
    title: "Lettering & Delivery",
    task: "Add dialogue, sound effects, perform final quality review, and deliver high-resolution PDF files.",
    timeline: "1 week",
  },
];

const selectClass =
  "w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white";
const labelClass = "block text-xs font-bold uppercase tracking-[0.2em] mb-2";

export default function ComicBooks() {
  const [cfg, setCfg] = useState({
    format: "Physical Copy",
    size: "6x9",
    binding: "Saddle Stitch",
    pages: 24,
    bookQty: 25,
    artStyle: "Watercolor",
  });
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const isPhysical = cfg.format === "Physical Copy";
  const isHyper = cfg.artStyle === "Hyper-Detailed";
  const price = isPhysical
    ? QTY_PRICES[cfg.bookQty] + (cfg.size === "6x9" ? 2790 : 0) + (isHyper ? 300 : 0)
    : isHyper
    ? 2799
    : 2499;

  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="comics-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Comic Books
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Comic Book Designs
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            Build your comic book — choose your size, binding, format and page count.
            Pricing starts at{" "}
            <span className="font-bold text-[#7BA7D9]">$2,989</span>.
          </p>
        </div>
      </section>

      {/* BUILDER */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div
            data-testid="comic-milestones"
            className="brutal-border brutal-shadow bg-[#3E6FA8] text-white p-7 sm:p-10 mb-8"
          >
            <h2 className="font-display text-3xl sm:text-4xl">Comic Book Milestones</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              {MILESTONES.map((m, i) => (
                <div key={m.title} className="border-l-2 border-white/30 pl-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                    Stage {i + 1}
                  </p>
                  <h3 className="font-display text-xl mt-1">{m.title}</h3>
                  <p className="mt-1 text-sm text-white/85">
                    <span className="font-semibold">Task:</span> {m.task}
                  </p>
                  <p className="mt-1 text-sm text-white/85">
                    <span className="font-semibold">Timeline:</span> {m.timeline}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* CONFIG */}
            <div
              className="lg:col-span-7 brutal-border brutal-shadow bg-white p-7 sm:p-9"
              data-testid="comic-builder"
            >
              <div className="flex items-center gap-4 mb-7">
                <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
                  <BookOpen size={26} strokeWidth={2} />
                </div>
                <h2 className="font-display text-3xl text-[#1F2530]">Build Your Comic</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Format</label>
                  <select data-testid="comic-format" value={cfg.format} onChange={set("format")} className={selectClass}>
                    {FORMATS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Comic Book Size</label>
                  <select data-testid="comic-size" value={cfg.size} onChange={set("size")} className={selectClass}>
                    {SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Art Style</label>
                  <select data-testid="comic-artstyle" value={cfg.artStyle} onChange={set("artStyle")} className={selectClass}>
                    {ART_STYLES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                {isPhysical && (
                  <div>
                    <label className={labelClass}>Binding</label>
                    <select data-testid="comic-binding" value={cfg.binding} onChange={set("binding")} className={selectClass}>
                      {BINDINGS.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Pages</label>
                  <select
                    data-testid="comic-pages"
                    value={cfg.pages}
                    onChange={(e) => setCfg((c) => ({ ...c, pages: Number(e.target.value) }))}
                    className={selectClass}
                  >
                    {PAGES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                {isPhysical && (
                  <div className="sm:col-span-2" data-testid="comic-bookqty-wrap">
                    <label className={labelClass}>Book Quantity</label>
                    <div className="flex flex-wrap gap-2">
                      {BOOK_QTYS.map((q) => (
                        <button
                          key={q}
                          data-testid={`comic-bookqty-${q}`}
                          onClick={() => setCfg((c) => ({ ...c, bookQty: q }))}
                          className={`min-w-[56px] px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                            cfg.bookQty === q
                              ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                              : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
                          }`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SUMMARY */}
            <div className="lg:col-span-5">
              <div className="brutal-border brutal-shadow bg-[#1F2530] text-white p-7 sm:p-9 sticky top-24">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
                  Your Comic Book
                </p>
                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Format</dt>
                    <dd className="font-semibold text-right" data-testid="summary-format">{cfg.format}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Size</dt>
                    <dd className="font-semibold">{cfg.size}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Art Style</dt>
                    <dd className="font-semibold">{cfg.artStyle}</dd>
                  </div>
                  {isPhysical && (
                    <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                      <dt className="text-white/60">Binding</dt>
                      <dd className="font-semibold">{cfg.binding}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Pages</dt>
                    <dd className="font-semibold">{cfg.pages}</dd>
                  </div>
                  {isPhysical && (
                    <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                      <dt className="text-white/60">Book Qty</dt>
                      <dd className="font-semibold">{cfg.bookQty}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-6 flex items-end justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                    Total
                  </span>
                  <span
                    data-testid="comic-price"
                    className="text-base font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-3 py-1.5 rounded-full"
                  >
                    ${price.toLocaleString()}
                  </span>
                </div>

                <Link
                  to="/contact"
                  state={{ service: "Comic Books" }}
                  data-testid="comic-order"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-6 py-4"
                >
                  Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
                </Link>
                {!isPhysical && (
                  <p className="mt-3 text-xs text-white/60">
                    Ebook (Digital Copy) — book quantity does not apply.
                  </p>
                )}
                <p
                  data-testid="comic-completion-note"
                  className="mt-4 text-sm font-semibold italic text-red-400"
                >
                  *Please Note: Comic book projects require 90–120 days for completion.
                  Updates are provided after each milestone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
